import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import { toast } from "react-toastify";
import { FaEdit, FaSearch } from "react-icons/fa";

const TeamLeadEmployeeData = () => {
  const { employee_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // --- Data States ---
  const [students, setStudents] = useState([]);
  const [employeeName, setEmployeeName] = useState("");
  const [loading, setLoading] = useState(false);

  // --- Filter States ---
  const [filterStatus, setFilterStatus] = useState("Interested");
  const [districtFilter, setDistrictFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- Pagination States ---
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // --- Auth/Role State ---
  const [memberType, setMemberType] = useState(null);

  // 1. Fetch Member Type (Role)
  useEffect(() => {
    const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
    const decodedMemberType = encodedMemberType ? atob(encodedMemberType) : null;
    setMemberType(decodedMemberType);
  }, []);

  const isTeamLead = useMemo(() => memberType === 'Team Lead', [memberType]);

  // 2. Fetch Employee Data
  useEffect(() => {
    const fetchEmployeeStudentData = async () => {
      setLoading(true);
      try {
        const teamLeadEmpId = atob(sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6"));

        const response = await fetch(SummaryApi.getEmpDataByTLIdndEmpId.url, {
          method: SummaryApi.getEmpDataByTLIdndEmpId.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify({
            team_lead_emp_id: teamLeadEmpId,
            assign_emp_id: employee_id,
          }),
        });

        if (!response.ok) throw new Error("Failed to fetch student data");

        const data = await response.json();
        setStudents(data?.data || []);

        if (data?.data && data.data.length > 0) {
          setEmployeeName(data.data[0].employee_name || "Unknown Employee");
        } else {
          setEmployeeName("Unknown Employee");
        }
        
        setCurrentPage(1);
      } catch (error) {
        toast.error(`Error fetching data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeStudentData();
  }, [employee_id]);

  // 3. Helper: Date Logic
  const filterByDateRange = (studentDate) => {
    if (!studentDate) return false;
    const date = new Date(studentDate);
    const today = new Date();

    // Reset hours for accurate comparison
    const resetTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dateNoTime = resetTime(date);
    const todayNoTime = resetTime(today);

    switch (dateFilter) {
      case "Today":
        return dateNoTime.getTime() === todayNoTime.getTime();

      case "Yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return dateNoTime.getTime() === resetTime(yesterday).getTime();
      }

      case "This Week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Sunday as start
        return date >= weekStart && date <= today;
      }

      case "Last Week": {
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return date >= lastWeekStart && date <= lastWeekEnd;
      }

      case "This Month":
        return (
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear()
        );

      case "Last Month": {
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);
        return (
          date.getMonth() === lastMonth.getMonth() &&
          date.getFullYear() === lastMonth.getFullYear()
        );
      }

      case "Custom":
        if (startDate && endDate) {
          const s = new Date(startDate);
          const e = new Date(endDate);
          e.setHours(23, 59, 59, 999); // Include full end day
          return date >= s && date <= e;
        }
        return true;

      default:
        return true;
    }
  };

  // 4. Memoized Counts
  const counts = useMemo(() => {
    const allCount = students.length;
    const mostInterestedCount = students.filter(s => s.is_interested === "Most Interested").length;
    const interestedCount = students.filter(s => s.is_interested === "Interested").length;
    return { allCount, mostInterestedCount, interestedCount };
  }, [students]);

  // 5. Unique Districts
  const districts = useMemo(() => 
    ["All", ...new Set(students.map((s) => s.district).filter(Boolean))],
  [students]);

  // 6. Master Filter Logic
  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      // Status Filter
      const statusMatch = filterStatus === "All" || student.is_interested === filterStatus;
      
      // District Filter
      const districtMatch = districtFilter === "All" || student.district === districtFilter;
      
      // Date Filter
      const dateMatch = filterByDateRange(student.created_date);

      // Search Filter
      const query = searchQuery.toLowerCase();
      const searchMatch = 
        searchQuery === "" ||
        (student.student_name && student.student_name.toLowerCase().includes(query)) ||
        (student.mobile_number1 && student.mobile_number1.includes(query)) ||
        (student.city && student.city.toLowerCase().includes(query)) ||
        (student.clg_name && student.clg_name.toLowerCase().includes(query)) ||
        (student.district && student.district.toLowerCase().includes(query));

      return statusMatch && districtMatch && dateMatch && searchMatch;
    });
  }, [students, filterStatus, districtFilter, dateFilter, startDate, endDate, searchQuery]);

  // 7. Pagination Logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredStudents.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredStudents.length / recordsPerPage);

  // --- Handlers ---
  const handlePrev = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  const handleNext = () => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

 const handleEditClick = async (student) => {
    // Fetch student details
    const responseDetails = await fetch(
      SummaryApi.getStudentDataByStudentId.url,
      {
        method: SummaryApi.getStudentDataByStudentId.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer sandeep_university_916",
        },
        body: JSON.stringify({ student_id: student.student_id }),
      }
    );
    const studentData = (await responseDetails.json()).data || null;

    // Fetch follow-up details
    let followUps = [];
    if (student.tl_student_followup_id) {
      const responseFollow = await fetch(
        SummaryApi.getStudentFollowDetails.url,
        {
          method: SummaryApi.getStudentFollowDetails.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify({
            tl_student_followup_id: student.tl_student_followup_id,
          }),
        }
      );
      const followData = await responseFollow.json();
      followUps = followData.data || [];
    }

    // Navigate to my-history page with state
    navigate("/my-history", {
      state: {
        student,
        studentDetails: studentData,
        studentFollowUps: followUps,
      },
    });
  };

  const maskMobileNumber = (number) => {
    if (!number) return "-";
    return "**********"; 
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      
      {/* --- Header --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Assigned Students</h2>
          <p className="text-sm text-gray-500 mt-1">
            Employee: <span className="font-semibold text-blue-600">{employeeName}</span>
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg shadow-sm hover:bg-gray-100 transition"
        >
          Back
        </button>
      </div>

      {/* --- Status Tabs --- */}
      <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
        {["All", "Most Interested", "Interested"].map((status) => (
          <button
            key={status}
            onClick={() => handleFilterChange(status)}
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition whitespace-nowrap ${
              filterStatus === status
                ? "bg-blue-600 text-white border-blue-600 shadow-md"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {status}{" "}
            <span
              className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                filterStatus === status
                  ? "bg-white text-blue-600"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {status === "All"
                ? counts.allCount
                : status === "Most Interested"
                ? counts.mostInterestedCount
                : counts.interestedCount}
            </span>
          </button>
        ))}
      </div>

      {/* --- Unified Filter Toolbar (Search + District + Date) --- */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          
          {/* 1. Search Bar */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search Name, Mobile, City, College..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>

          {/* 2. Filters Group */}
          <div className="flex flex-col sm:flex-row gap-3">
            
            {/* District Filter */}
            <select
              value={districtFilter}
              onChange={(e) => {
                setDistrictFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px] cursor-pointer"
            >
              {districts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist === "All" ? "Filter District" : dist}
                </option>
              ))}
            </select>

            {/* Date Filter Dropdown */}
            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px] cursor-pointer"
            >
              <option value="All">All Time</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
              <option value="Last Week">Last Week</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="Custom">Custom Range</option>
            </select>

            {/* Custom Date Inputs */}
            {dateFilter === "Custom" && (
              <div className="flex items-center gap-2 animate-fadeIn">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-400 font-medium">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- Table --- */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-700 font-semibold border-b border-gray-200">
            <tr>
              <th className="py-3 px-4 whitespace-nowrap">SNO</th>
              <th className="py-3 px-4 whitespace-nowrap">Student Name</th>
              <th className="py-3 px-4 whitespace-nowrap">District</th>
              <th className="py-3 px-4 whitespace-nowrap">City</th>
              <th className="py-3 px-4 whitespace-nowrap">College Name</th>
              <th className="py-3 px-4 whitespace-nowrap">Mobile Number</th>
              <th className="py-3 px-4 whitespace-nowrap">Enrolled Date</th>
              <th className="py-3 px-4 whitespace-nowrap">Current Status</th>
              <th className="py-3 px-4 whitespace-nowrap">Lead By</th>
              {isTeamLead && <th className="py-3 px-4 whitespace-nowrap">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isTeamLead ? "10" : "9"} className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <p className="mt-2 text-gray-500">Loading data...</p>
                </td>
              </tr>
            ) : currentRecords.length === 0 ? (
              <tr>
                <td colSpan={isTeamLead ? "10" : "9"} className="text-center py-8 text-gray-500">
                  <div className="flex flex-col items-center">
                    <FaSearch className="text-gray-300 text-3xl mb-2" />
                    <p>No data found matching your criteria.</p>
                  </div>
                </td>
              </tr>
            ) : (
              currentRecords.map((student, index) => (
                <tr
                  key={index}
                  className={`border-b border-gray-100 transition duration-150 ${
                    student.next_followup_date_time
                      ? "bg-green-50 hover:bg-green-100"
                      : index % 2 === 0
                      ? "bg-white hover:bg-gray-50"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <td className="py-3 px-4">{indexOfFirstRecord + index + 1}</td>
                  <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                    {student.student_name || "-"}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.district || "-"}</td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.city || "-"}</td>
                  <td className="py-3 px-4 max-w-xs truncate" title={student.clg_name}>
                    {student.clg_name || "-"}
                  </td>
                  <td className="py-3 px-4 font-mono text-gray-600 whitespace-nowrap" title={student.mobile_number1}>
                    {student.mobile_number1}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.created_date || "-"}</td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        student.is_interested === "Most Interested"
                          ? "bg-green-100 text-green-700"
                          : student.is_interested === "Interested"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {student.is_interested || "-"}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{student.employee_name || "-"}</td>
                  {isTeamLead && (
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-xs font-medium transition shadow-sm flex items-center gap-1"
                        onClick={() => handleEditClick(student)}
                      >
                        <FaEdit /> Followup
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination --- */}
      {filteredStudents.length > recordsPerPage && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          >
            Previous
          </button>

          <span className="px-4 py-1.5 border border-blue-600 rounded-lg bg-blue-600 text-white text-sm font-medium shadow-sm">
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamLeadEmployeeData;